import AsyncDataTable from "@/components/core/async-data-table/async-data-table";
import {
  DatableColumnType,
  FilterType,
  propertyType,
} from "@/components/core/async-data-table/type";
import { endpoints } from "@/api/endpoints/endpoints";
import FilterHeader from "@/components/core/async-data-table/filter-header";
import ActionPopover from "@/components/action/action-popover";
import { Plus } from "lucide-react";
import { useLocation } from "wouter";
import { useDeleteMessageTemplate } from "@/services/message-template-service";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { sanitizeHtml } from "@/utils/sanitize-html";

const MessageTemplateAsyncTable = () => {
  const [, setLocation] = useLocation();
  const deleteMessageTemplate = useDeleteMessageTemplate();
  const [refreshPage, setRefreshPage] = useState(false);
  const { toast } = useToast();

  const handleDelete = async (messageTemplateId: string) => {
    try {
      await deleteMessageTemplate.execute(messageTemplateId);
      toast({
        title: "Success",
        description: "Message template deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete message template",
        variant: "destructive",
      });
    }

    setRefreshPage(!refreshPage);
  };

  const cols: DatableColumnType[] = [
    {
      name: "key",
      id: "key",
      label: "Key",
      searchable: true,
      orderable: true,
      datatype: "string",
    },
    {
      name: "name",
      id: "name",
      label: "Name",
      searchable: true,
      orderable: true,
      datatype: "string",
    },
    {
      name: "category",
      id: "category",
      label: "Category",
      searchable: true,
      orderable: true,
      datatype: "string",
    },
    {
      name: "content",
      id: "content",
      label: "Content",
      searchable: true,
      orderable: false,
      datatype: "custom",
      Render: ({ row }: { row: any }) => {
        const safeHtml = sanitizeHtml(String(row.content ?? ""));
        return (
          <span
            className="text-sm text-gray-700 font-medium leading-relaxed"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        );
      },
    },
    {
      name: "action",
      id: "action",
      label: "Action",
      Render: ({ row }: { row: any }) => (
        <ActionPopover
          onEdit={() => {
            setLocation(`/message-templates/${row.id}/edit`);
          }}
          onDelete={() => {
            handleDelete(row.id);
          }}
        />
      ),
    },
  ];

  const defaultFilterProp: propertyType[] = [
    { propName: "key", value: "", type: FilterType.STRING },
    { propName: "name", value: "", type: FilterType.STRING },
    { propName: "category", value: "", type: FilterType.STRING },
    { propName: "content", value: "", type: FilterType.STRING },
  ];

  return (
    <AsyncDataTable
      dataUrl={endpoints.messageTemplate.list}
      columns={cols}
      autoIndex
      FilterComponent={FilterHeader}
      refreshPage={refreshPage}
      defaultFilterProp={defaultFilterProp}
      showPageHeader={false}
      pageTitle="Message Templates"
      pageSubtitle="Manage your message templates"
      headerAction={() => {}}
      headerActionText="Create Template"
      headerActionIcon={<Plus size={18} />}
    />
  );
};

export default MessageTemplateAsyncTable;
